import confetti from "canvas-confetti";
import { Theme, createTheme } from "@mui/material/styles";
import { logger } from "@platform/browser-logger";

import { zIndexTokens } from "../../tokens";

import type {
  BitmapShape,
  Options as ConfettiOptions,
  PathShape,
  Shape,
} from "canvas-confetti";

/**
 * Default confetti configuration options
 * TODO: Move this to app theme customization layer someday
 */
const DEFAULT_CONFETTI_OPTIONS: ConfettiOptions = {
  particleCount: 50,
  spread: 120,
  ticks: 70,
  startVelocity: 20,
  zIndex: zIndexTokens.tooltip,
};

// Default colors for confetti if no theme is provided
const DEFAULT_CONFETTI_COLORS = ["#ff4081", "#3f51b5", "#4caf50"];

// Default word and shapes
const DEFAULT_CONFETTI_WORD = "CENTRO";
const BASIC_CONFETTI_SHAPES = ["star", "circle", "square"];

/**
 * Safely attempts to create a theme.
 * Returns undefined if MUI is unavailable or createTheme fails.
 *
 * This is MUI's default theme, not the app's: it has no `brandPrimary` /
 * `brandSupporting`, so the colors below read its own `primary` and `secondary`.
 */
const getCurrentTheme = (): Theme | undefined => {
  try {
    if (typeof createTheme !== "function") {
      logger.warn("MUI createTheme function is not available.");
      return undefined;
    }

    return createTheme();
  } catch (error) {
    logger.error("Failed to create theme", error as Error);
    return undefined;
  }
};

/**
 * Theme-aware confetti configuration options
 */
export interface ThemeConfettiOptions extends ConfettiOptions {
  theme?: Theme;
  customWord?: string;
}

/**
 * Generates confetti shapes from a word using theme colors
 * @param word - The word to convert to shapes
 * @param theme - Optional MUI theme to use for colors
 * @returns Array of confetti shapes
 */
const shapesFromWord = (
  word: string,
  theme?: Theme,
): (string | PathShape | BitmapShape)[] => {
  // Extract colors from the theme if available, otherwise use defaults
  const colors = theme
    ? [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.success.main,
      ]
    : DEFAULT_CONFETTI_COLORS;

  // Map each character to a shape with a random theme color
  const letterShapes = Array.from(word).map((letter) => {
    const letterColor = colors[Math.floor(Math.random() * colors.length)];
    return confetti.shapeFromText({
      text: letter,
      color: letterColor,
    });
  });
  return [...letterShapes, ...BASIC_CONFETTI_SHAPES];
};

/**
 * Extracts theme colors for confetti
 * @param theme - Optional MUI theme object
 * @returns Array of color strings
 */
const getThemeColors = (theme?: Theme): string[] =>
  // Return theme colors if theme is available, otherwise use defaults
  theme
    ? [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.success.main,
      ]
    : DEFAULT_CONFETTI_COLORS;
/**
 * Triggers a confetti effect originating from the specified element.
 *
 * @param elementId - The ID of the element where the confetti originates
 * @param options - Optional configuration for the confetti effect
 * @returns A Promise that resolves when the animation is complete, or null if the element wasn't found
 *
 * @example
 * // Basic usage with default settings
 * triggerConfetti('button-id');
 *
 * @example
 * // With custom options
 * triggerConfetti('celebration-element', {
 *   particleCount: 100,
 *   customWord: 'HOORAY'
 * });
 *
 * @example
 * // Using with async/await
 * async function celebrate() {
 *   await triggerConfetti('win-button');
 *   showNextScreen();
 * }
 */
export const triggerConfetti = (
  elementId: string,
  options?: ThemeConfettiOptions,
): Promise<void> | null => {
  const element = document.getElementById(elementId);
  if (!element) {
    logger.warn(`Element with ID '${elementId}' not found.`);
    return null;
  }

  // Calculate origin position based on the element's position
  const rect = element.getBoundingClientRect();
  const origin = {
    x: (rect.left + rect.width / 2) / window.innerWidth,
    y: (rect.top + rect.height / 2) / window.innerHeight,
  };

  // Try to get theme from options first, then from context
  const theme = options?.theme || getCurrentTheme();
  const word = options?.customWord || DEFAULT_CONFETTI_WORD;

  // Core confetti configuration
  const confettiConfig: ConfettiOptions = {
    ...DEFAULT_CONFETTI_OPTIONS,
    origin,
    shapes: shapesFromWord(word, theme) as Shape[],
    colors: getThemeColors(theme),
  };

  // Merge with user options, excluding our custom properties
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { theme: _, customWord: __, ...userOptions } = options || {};

  // Run the confetti with merged options
  return confetti({
    ...confettiConfig,
    ...userOptions,
  });
};
